// issue-analysis's inputs to createDashboard (@rdrive/plugin-dashboard-shell; IA_ABSORPTION_PLAN
// Phase 3 steps 8–9 — the shape project-overview's took in Phase 1 step 6b). main.js is the global
// CSS plus createDashboard(dashboardConfig), the `$launchUrl` provide and mount; createDashboard
// creates the DASHBOARD from what is here — the platform settings, the proxy transport, DuckDB, the
// data loader over the manifest, the two filter contexts, analytics — then the router, i18n and
// Vuetify, calls the store factory with the dashboard, and installs router → Vuetify → store →
// i18n (main.js's old order). Components reach the dashboard with useDashboard() (pages through
// composables/useAppContext.js, project-overview's shape). A JS module, not JSON: it holds
// components and functions (PLATFORM.md §3.4).
//
// IMPORT ORDER IS LOAD-BEARING FOR CSS: App first, then the pages in the order the old router.js
// imported them, then `vuetify/components` — the order main.js + router.js evaluated them in. The
// global CSS stays first, in main.js.
import App from './App/component.vue';
import ProjectPage from './components/ProjectPage/component.vue';
import DrawingsPage from './components/DrawingsPage/component.vue';
import AnalysisPage from './components/AnalysisPage/component.vue';
import { THEME_LIGHT } from '@rdrive/theme';
import { vocabulary } from '@rdrive/vocabulary';
import { fa } from 'vuetify/iconsets/fa';
import { mdi } from 'vuetify/iconsets/mdi';
import { md } from 'vuetify/iconsets/md';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { createAppStore } from './store';
import messages from './locales/index.js';
import { manifest } from './data/manifest';
import { issuesFilterConfig, statusHistoryFilterConfig } from './data/filters';
import { PROXY_URL, API_URL, GA_MEASUREMENT_ID } from './config/constants';

// The app's pages (was src/router.js, which had no guards — only the GA afterEach, which
// createDashboard now registers from `analytics`). Route NAMES are the app bar's nav keys
// (App/setup.js `menuItems` → DashboardShell's `navItems`).
const routes = [
  { path: '/', redirect: '/project' },
  { path: '/project', name: 'Project', component: ProjectPage },
  { path: '/drawings', name: 'Drawings', component: DrawingsPage },
  { path: '/analysis', name: 'Analysis', component: AnalysisPage },
];

// The domain store, created by createDashboard through the factory below (its actions need the
// dashboard's DuckDB connection). Bound here for the data loader's onLoaded hook, which runs long
// after — at the data load (the welcome dialog's Load button).
let store = null;

export default {
  // The root component: issue-analysis's adapter over DashboardShell. Its onMounted calls
  // `dashboard.start()` (the platform half of the boot) and the welcome dialog's Load button
  // runs `dashboard.loader.load()`.
  App,

  routes,
  history: 'web', // createWebHistory(), no base — as router.js had it

  // The Vuex store — DOMAIN state only (store/index.js; since 2026-10-13 only what something
  // reads). A factory: createDashboard calls it with the dashboard.
  store: (dashboard) => (store = createAppStore(dashboard)),

  // The proxy host and the default API host (settings.apiUrl until the URL's `apiUrl` lands).
  transport: { proxyUrl: PROXY_URL, apiUrl: API_URL },

  // The data manifest (15 boot tables; status_history_temporal and the status_templates re-rank
  // as ddl.post) and the domain half of the load, run after the tables land, with the query gate
  // open, and BEFORE `dataLoaded` flips — the root component's old post-load block, in its order:
  // both filter contexts, then the logo and the status templates (those two used to run just AFTER
  // `dataLoaded` flipped; now nothing sees dataLoaded=true with an empty status-template list).
  data: {
    manifest,
    onLoaded: async ({ filters }) => {
      // Both contexts initialise through DuckDB's GATED `conn.query`, which the loader opened
      // before this hook. A failing init statement (e.g. status_history_temporal missing because
      // its endpoint failed) is logged and swallowed there WITHOUT shutting the gate, so a partial
      // load keeps rendering what did load. (The classes these configs replaced initialised
      // through `conn.loadData`, which does shut it — hence the `duckdb.isDataLoaded = true` that
      // followed here until Phase 4 step 11.)
      await filters.issues.initializeFilterContext();
      await filters.statusHistory.initializeFilterContext();
      await store.dispatch('loadLookupObjectsFromDB');
    },
  },

  // The two filter contexts: createFilterContext configs (data/filters.js — the schema, the views,
  // the issues table's column detection; IA_ABSORPTION_PLAN Phase 4 step 11, which replaced this
  // app's own FilterIssuesContext / FilterStatusHistoryContext classes). The dashboard builds one
  // context per entry over its DuckDB connection; both are initialised by onLoaded above.
  filters: {
    issues: issuesFilterConfig,
    statusHistory: statusHistoryFilterConfig,
  },

  // GA4 through @rdrive/helper-analytics (it loads the tag — index.html no longer does): `config`
  // once the URL settings are in (dashboard.start()), a page view per navigation (router.js's
  // afterEach), and `parent_url` = settings.apiUrl on every hit (the old Vuex module's context).
  analytics: { measurementId: GA_MEASUREMENT_ID },

  // window.__rdriveDev (the token-refresh hook tooling/smoke/10-token-refresh.js drives) in dev only
  dev: import.meta.env.DEV,

  // @rdrive/theme's createDashboardVuetify options. Its two themes are the light/dark definitions
  // main.js inlined — the same colours, value for value. The wildcard component/directive maps and
  // the icon sets are main.js's, unchanged (PACKAGING_PLAN.md §3.4).
  vuetify: {
    defaultTheme: THEME_LIGHT,
    icons: {
      defaultSet: 'mdi',
      sets: {
        mdi,
        md,
        fa,
      },
    },
    components,
    directives,
  },

  // Root i18n: composition mode, fallback 'en' (createDashboard's fixed policy). The app's page and
  // feature text (src/locales/) OVER rdrive's shared words — one home per key since Phase 4 step 14
  // (the app's 39 vocabulary copies are gone; `pnpm i18n:extract` reports OVERRIDES). Its locale
  // follows the dashboard's settings.locale, normalised once by the dashboard (@rdrive/i18n's
  // normalizeLocale).
  i18n: {
    messages,
    vocabulary,
  },
};
